import api from '@/api';

import {
  WorkOrderItem,
  CreateWorkOrderItemRequest,
  UpdateWorkOrderItemRequest,
  ListWorkOrderItemResult,
  DeleteWorkOrderItemRequest,
  ChangeWorkOrderItemDiscountRequest,
  CreateWorkOrderItemListRequest,
  WorkOrderItemType,
  ChangeWorkOrderItemTagRequest,
  ChangeWorkOrderItemRestoreRequest,
  WorkOrderItemHistoryRequest,
  WorkOrderItemHistory,
  WorkOrderPriceDiferenceListRequest,
  WorkOrderPriceDiference,
  WorkOrderItemsSwapOperationalPart,
  WorkOrderItemBenefitsCreateRequest,
  WorkOrderItemBenefitsRemoveRequest,
  WorkOrderItemRefusePublicRequest,
  WorkOrderItemBatchRemoveRequest,
  WorkOrderItemActionListRequest,
  WorkOrderItemActionListResponse,
  WorkOrderItemsBatchRestore,
} from './WorkOrdersItems.types';

import { API_BASE_URL } from '..';
import { PageResult } from '@/core/request/PageResult';

const CONTROLLER_URL = `${API_BASE_URL}/workorders/complements`;

export async function listWorkOrderItems(
  complementId?: number,
  showRemoved = false,
  itemType?: WorkOrderItemType,
) {
  const result = (await api.get(`${CONTROLLER_URL}/${complementId}/itens`, {
    showRemoved,
    itemType,
  })) as unknown;

  return result as ListWorkOrderItemResult;
}

export async function getWorkOrderItem(id: number): Promise<WorkOrderItem> {
  const result = (await api.get(`${CONTROLLER_URL}/${id}`, null)) as unknown;

  return result as WorkOrderItem;
}

export async function createWorkOrderItem(
  obj: CreateWorkOrderItemRequest,
): Promise<WorkOrderItem> {
  const result = (await api.post(
    `${CONTROLLER_URL}/${obj.complementId}/itens`,
    obj,
    null,
  )) as unknown;

  return result as WorkOrderItem;
}

export async function createWorkOrderItemList({
  complementId,
  ...data
}: CreateWorkOrderItemListRequest) {
  const result = (await api.post(
    `${CONTROLLER_URL}/${complementId}/itens/list`,
    data,
    null,
  )) as unknown;

  return result as void;
}

export async function updateWorkOrderItem(
  obj: UpdateWorkOrderItemRequest,
): Promise<WorkOrderItem> {
  const result = (await api.put(
    `${CONTROLLER_URL}/${obj.workOrderComplementId}/itens`,
    obj,
    null,
  )) as unknown;

  return result as WorkOrderItem;
}

export async function deleteWorkOrderItem(req: DeleteWorkOrderItemRequest) {
  const result = (await api.remove(
    `${CONTROLLER_URL}/${req.complementId}/itens/${req.workOrderItemId}`,
    req,
    null,
  )) as unknown;

  return result;
}

export async function changeWorkOrderItemDiscount(
  obj: ChangeWorkOrderItemDiscountRequest,
): Promise<ListWorkOrderItemResult> {
  const result = (await api.put(
    `${CONTROLLER_URL}/${obj.complementId}/itens/changeDiscount`,
    obj,
    null,
  )) as unknown;

  return result as ListWorkOrderItemResult;
}

export async function changeWorkOrderItemTag(
  obj: ChangeWorkOrderItemTagRequest,
) {
  const result = (await api.patch(
    `${CONTROLLER_URL}/${obj.complementId}/itens/${obj.itemId}/tag`,
    obj,
    null,
  )) as unknown;

  return result as void;
}

export async function changeWorkOrderItemRestore(
  obj: ChangeWorkOrderItemRestoreRequest,
) {
  const result = (await api.post(
    `${CONTROLLER_URL}/${obj.complementId}/itens/restore`,
    obj,
    null,
  )) as unknown;

  return result as void;
}

export async function listWorkOrderItemHistory(
  data?: WorkOrderItemHistoryRequest,
) {
  const result = (await api.get(
    `${CONTROLLER_URL}/${data?.complementId}/itens/PartHistory`,
    data,
  )) as unknown;

  return result as PageResult<WorkOrderItemHistory>;
}

export async function listWorkOrderPriceDiference(
  params?: WorkOrderPriceDiferenceListRequest,
) {
  const result = (await api.get(
    `${CONTROLLER_URL}/${params?.complementId}/itens/priceDiference/${params?.companyId}`,
    null,
  )) as unknown;

  return result as WorkOrderPriceDiference[];
}

export async function workOrderItemsSwapOperationPart(
  data: WorkOrderItemsSwapOperationalPart,
) {
  const result = (await api.post(
    `${CONTROLLER_URL}/${data.complementId}/itens/swap-operational-part`,
    data,
  )) as unknown;

  return result as void;
}

export async function workOrderItemBenefitsCreate({
  complementId,
  ...data
}: WorkOrderItemBenefitsCreateRequest) {
  const result = (await api.post(
    `${CONTROLLER_URL}/${complementId}/itens/add-item-from-benefit`,
    data,
  )) as unknown;
  return result as void;
}

export async function workOrderItemBenefitsRemove(
  params: WorkOrderItemBenefitsRemoveRequest,
) {
  const result = (await api.remove(
    `${CONTROLLER_URL}/${params.complementId}/itens/${params.itemId}/item-by-benefit`,
    {},
    null,
  )) as unknown;
  return result as void;
}

export async function workOrderItemRefusePublic(
  params: WorkOrderItemRefusePublicRequest,
) {
  const result = (await api.remove(
    `${CONTROLLER_URL}/${params.complementId}/itens/${params.workOrderItemId}/public`,
    params,
    null,
  )) as unknown;
  return result as void;
}

export async function workOrderHasRemovedItems(complementId?: number) {
  const result = (await api.get(
    `${CONTROLLER_URL}/${complementId}/itens/has-removed-items`,
    null,
  )) as unknown;

  return result as boolean;
}

export async function workOrderItemBatchRemove(
  request: WorkOrderItemBatchRemoveRequest,
) {
  const result = (await api.remove(
    `${CONTROLLER_URL}/${request.complementId}/itens/batch-remove`,
    {
      complementId: request.complementId,
      items: request.items.map((item) => ({
        workOrderItemId: item.id,
        isItemRejectedByCustomer: false,
      })),
    },
    null,
  )) as unknown;

  return result;
}

export async function workOrderItemActionList({
  complementId,
  ...payload
}: WorkOrderItemActionListRequest) {
  const result = (await api.get(
    `${CONTROLLER_URL}/${complementId}/itens/actions`,
    payload,
  )) as unknown;

  return result as WorkOrderItemActionListResponse;
}

export async function workOrderItemBatchRestore({
  complementId,
  ...payload
}: WorkOrderItemsBatchRestore) {
  const result = (await api.post(
    `${CONTROLLER_URL}/${complementId}/itens/batch-restore`,
    payload,
  )) as unknown;

  return result as void;
}
